// ============================================================================
// OrgNodeCard — carte personne vue etendue (NAV v3 aligne sur OrgFocusCard)
// ============================================================================
// Reutilise OrgFocusCard (tailles fixes, tokens v3). Clic = drill-in ; bouton
// replier/deplier frere de la carte (pan-safe sur le canvas).

import { ChevronDown, ChevronUp } from 'lucide-react';
import type { OrgNode } from '../../lib/org/hierarchy';
import { useTranslation } from '../../i18n';
import { OrgFocusCard, type OrgFocusCardVariant } from './OrgFocusCard';

function resolveVariant(
  node: OrgNode,
  depth: number,
  focusedEmail?: string | null,
): OrgFocusCardVariant {
  const isRoot = depth === 0;
  const isFocusedNode = focusedEmail === node.email;
  if (isFocusedNode || isRoot) return 'focus';
  return 'report';
}

export function OrgNodeCard({
  node,
  depth,
  isCollapsed,
  isHighlighted,
  isFocused,
  focusedEmail,
  onToggle,
  onDrillIn,
}: {
  node: OrgNode;
  depth: number;
  isCollapsed: boolean;
  isHighlighted?: boolean;
  isFocused?: boolean;
  focusedEmail?: string | null;
  onToggle: () => void;
  onDrillIn: () => void;
}) {
  const { t } = useTranslation();
  const isRoot = depth === 0;
  const displayName = node.full_name ?? node.email;
  const variant = resolveVariant(node, depth, focusedEmail ?? (isFocused ? node.email : null));

  return (
    <div id={`org-node-${node.email}`} className="relative pb-3">
      <OrgFocusCard
        node={node}
        variant={variant}
        isHighlighted={isHighlighted || isFocused}
        showAccentBar={isRoot}
        onFocus={onDrillIn}
      />

      {node.descendant_count > 0 ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          aria-expanded={!isCollapsed}
          aria-label={
            isCollapsed
              ? t('gest:organigramme.node.expandBranch', {
                  name: displayName,
                  count: node.descendant_count,
                })
              : t('gest:organigramme.node.collapseBranch', { name: displayName })
          }
          className="absolute -bottom-2.5 left-1/2 z-10 inline-flex h-5 min-w-5 -translate-x-1/2 touch-manipulation items-center justify-center gap-0.5 rounded-full border px-1.5 text-chip tabular-nums shadow-xs transition-colors dark:bg-slate-800 dark:text-slate-300 dark:hover:text-slate-100 border-slate-200 bg-white text-slate-600 hover:text-slate-900 dark:border-slate-700"
        >
          {isCollapsed ? (
            <>
              <ChevronDown size={12} aria-hidden="true" />
              {node.descendant_count}
            </>
          ) : (
            <ChevronUp size={12} aria-hidden="true" />
          )}
        </button>
      ) : null}
    </div>
  );
}
